/**
 * Converts any of these formats to DD/MM/YYYY:
 *   YYYY-MM-DD   (API date field, e.g. "2026-10-07")
 *   MM-DD-YYYY   (MIS snapshot format stored by the backend)
 *   ISO datetime (e.g. "2026-10-07T12:34:56Z")
 * Returns the original string unchanged when it cannot be parsed.
 */
export const formatDMY = (value: any): string => {
  if (!value) return '';
  const s = String(value).trim();

  // MM-DD-YYYY  (backend MIS snapshot format)
  const mdyMatch = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (mdyMatch) return `${mdyMatch[2]}/${mdyMatch[1]}/${mdyMatch[3]}`;

  // YYYY-MM-DD  or ISO datetime starting with YYYY-MM-DD
  const ymdMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch) return `${ymdMatch[3]}/${ymdMatch[2]}/${ymdMatch[1]}`;

  return s; // unknown format — return as-is
};

/**
 * Like formatDMY but appends HH:MM from an ISO datetime string.
 * e.g. "2026-10-07T14:05:00Z" → "07/10/2026 14:05"
 * Falls back to formatDMY when there is no time component.
 */
export const formatDMYHM = (value: any): string => {
  if (!value) return '';
  const s = String(value).trim();
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]} ${isoMatch[4]}:${isoMatch[5]}`;
  }
  return formatDMY(s);
};

export const toDateTimeLocal = (isoString: any) => {
  if (!isoString) return;
  try {
    const date = new Date(isoString);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM"
  } catch (e) {
    console.error('Error converting date: ', isoString);
  }
};

export const formatDateYMDHM = (isoString: any) => {
  if (!isoString) return;

  try {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = (`0${date.getMonth() + 1}`).slice(-2);
    const day = (`0${date.getDate()}`).slice(-2);
    const hours = (`0${date.getHours()}`).slice(-2);
    const minutes = (`0${date.getMinutes()}`).slice(-2);
  
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch (e) {
    console.error('Error converting date YMDHM: ', isoString);
  }
};

export const formatDate = (date: any) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const formatYMD = (dateString: any) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
  });
};

export const formatMD = (dateString: any) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

export const getISTDateString = (date: any) => {
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(date.getTime() + istOffset);
  return istDate.toISOString().split("T")[0];
};


export const normalizeDateTime = (val: any) => {
  if (!val) return null;
  const date = new Date(val);
  return date.toISOString();
};
